/**
 * Stock item photos (tidaltech/koi#8079). Leatherback takes JPEG, PNG or
 * WebP up to 3 MB; phone photos are often bigger, so they are shrunk here
 * before upload.
 */
export const STOCK_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_STOCK_PHOTO_BYTES = 3 * 1024 * 1024;
const MAX_SIDE = 1600;

/** Whether a photo can be uploaded as it is. */
export function canUploadAsIs(photo: { type: string; size: number }): boolean {
  return (
    (STOCK_PHOTO_TYPES as readonly string[]).includes(photo.type) &&
    photo.size <= MAX_STOCK_PHOTO_BYTES
  );
}

/** The size that fits within maxSide on its longest side, keeping the shape. */
export function fittedSize(
  width: number,
  height: number,
  maxSide = MAX_SIDE,
): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * Returns the photo to upload: the file itself when it already fits,
 * otherwise a JPEG at most 1600 px on its longest side.
 */
export async function preparePhoto(file: File): Promise<Blob> {
  if (canUploadAsIs(file)) return file;
  const bitmap = await createImageBitmap(file);
  const { width, height } = fittedSize(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot resize the photo.');
  // JPEG has no transparency; a white background beats black.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.85),
  );
  if (!blob) throw new Error('The photo could not be resized.');
  if (blob.size > MAX_STOCK_PHOTO_BYTES) throw new Error('The photo is too large, even resized.');
  return blob;
}

/** The blob's bytes as base64, for JSON bodies. */
export async function blobToBase64(blob: Blob): Promise<string> {
  return bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
